/**
 * GenerateButton
 *
 * Primary action button with four visual states:
 *   idle     → "Generate Subtitles"
 *   uploading → "Uploading…"
 *   polling  → "Processing…" + cancel link
 *   error    → "Retry"
 */

import { memo } from 'react';
import { Loader2, Wand2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useSubtitles } from '../SubtitlesContext.context';

export const GenerateButton = memo(function GenerateButton() {
  const { t } = useTranslation();
  const { state, isUploading, isGenerating, handleGenerate, handleCancel } = useSubtitles();
  const { status, targetLanguages, mediaFile } = state;

  const canGenerate = Boolean(mediaFile) && targetLanguages.length > 0;
  const isWorking = isUploading || isGenerating;

  const label = (() => {
    if (status === 'uploading')
      return t('subtitles.generate.uploading', { defaultValue: 'Uploading…' });
    if (isGenerating)
      return t('subtitles.generate.polling', { defaultValue: 'Generating subtitles…' });
    if (status === 'error') return t('subtitles.generate.retry', { defaultValue: 'Retry' });
    return t('subtitles.generate.button', { defaultValue: 'Generate Subtitles' });
  })();

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={() => void handleGenerate()}
        disabled={isWorking || !canGenerate}
        className="btn-primary px-8 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
      >
        {isWorking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Wand2 className="h-5 w-5" />}
        <span>{label}</span>
      </button>

      {isGenerating && (
        <button
          onClick={() => void handleCancel()}
          className="text-sm text-white/50 hover:text-white/80 underline transition-colors"
        >
          {t('subtitles.generate.cancel', { defaultValue: 'Cancel' })}
        </button>
      )}

      {!canGenerate && !isWorking && (
        <p className="text-xs text-white/40 text-center">
          {!mediaFile
            ? t('subtitles.generate.uploadFirst', { defaultValue: 'Upload a file to get started.' })
            : t('subtitles.generate.selectLanguage', {
                defaultValue: 'Select at least one target language.',
              })}
        </p>
      )}
    </div>
  );
});
